import React, { useState, useEffect } from 'react';
import { Upload, FileText, AlertCircle, Mail, Download } from 'lucide-react';
import { API_URL } from '../../config';

export default function InvoicesView({ adminData }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'stripe', 'astria', 'manual'
  
  // Upload form state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadData, setUploadData] = useState({
    vendor_name: 'Astria.ai',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    amount_gross: '',
    vat_rate: '19.0'
  });

  useEffect(() => {
    fetchInvoices();
  }, [filter]);

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem('snapshot_token');
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('source', filter);
      }
      
      const res = await fetch(`${API_URL}/api/tax/invoices/list?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch invoices');
      
      const data = await res.json();
      setInvoices(data.invoices || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      alert('Bitte wähle eine PDF-Datei aus');
      return;
    }

    if (!uploadData.amount_gross || parseFloat(uploadData.amount_gross) <= 0) {
      alert('Bitte gib einen gültigen Brutto-Betrag ein');
      return;
    }

    try {
      setUploading(true);
      const token = localStorage.getItem('snapshot_token');
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('source', 'manual');
      formData.append('vendor_name', uploadData.vendor_name);
      formData.append('invoice_number', uploadData.invoice_number || `MANUAL-${Date.now()}`);
      formData.append('invoice_date', uploadData.invoice_date);
      formData.append('amount_gross', uploadData.amount_gross);
      formData.append('vat_rate', uploadData.vat_rate);

      const res = await fetch(`${API_URL}/api/tax/invoices/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) throw new Error('Upload failed');

      alert('Rechnung erfolgreich hochgeladen!');
      
      // Reset form
      setUploadFile(null);
      setUploadData({
        vendor_name: 'Astria.ai',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        amount_gross: '',
        vat_rate: '19.0'
      });
      
      // Refresh list
      fetchInvoices();
    } catch (error) {
      console.error('Error uploading invoice:', error);
      alert('Fehler beim Hochladen der Rechnung');
    } finally {
      setUploading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const colors = {
      'paid': 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/50',
      'pending': 'bg-yellow-500/20 text-yellow-500 border-yellow-500/50',
      'overdue': 'bg-red-500/20 text-red-500 border-red-500/50'
    };
    
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border-2 ${colors[status] || colors.pending}`}>
        {status?.toUpperCase()}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-black grain-texture text-white p-4 md:p-6 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 md:gap-4 mb-2">
            <FileText className="w-6 h-6 md:w-8 md:h-8 text-[#10B981]" />
            <h1 className="text-2xl md:text-4xl font-bold">Eingangsrechnungen</h1>
          </div>
          <p className="text-white/60 text-sm">Stripe & Astria Rechnungen verwalten</p>
        </div>

        {/* Upload Section */}
        <div className="mb-8 p-4 md:p-6 bg-black border-2 border-white/10 rounded-xl grain-texture">
          <h2 className="text-xl md:text-2xl font-bold mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 md:w-6 md:h-6 text-[#10B981]" />
            Rechnung hochladen
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">PDF-Datei *</label>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setUploadFile(e.target.files[0])}
                className="w-full px-4 py-3 bg-white/5 border-2 border-white/20 rounded-xl text-white"
                data-testid="invoice-file-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">Anbieter *</label>
              <select
                value={uploadData.vendor_name}
                onChange={(e) => setUploadData({...uploadData, vendor_name: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border-2 border-white/20 rounded-xl text-white"
                data-testid="invoice-vendor-select"
              >
                <option value="Astria.ai">Astria.ai</option>
                <option value="Stripe">Stripe</option>
                <option value="Sonstiges">Sonstiges</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">Rechnungsnummer</label>
              <input
                type="text"
                value={uploadData.invoice_number}
                onChange={(e) => setUploadData({...uploadData, invoice_number: e.target.value})}
                placeholder="z.B. INV-2025-001"
                className="w-full px-4 py-3 bg-white/5 border-2 border-white/20 rounded-xl text-white"
                data-testid="invoice-number-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">Rechnungsdatum *</label>
              <input
                type="date"
                value={uploadData.invoice_date}
                onChange={(e) => setUploadData({...uploadData, invoice_date: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border-2 border-white/20 rounded-xl text-white"
                data-testid="invoice-date-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">Brutto-Betrag (€) *</label>
              <input
                type="number"
                step="0.01"
                value={uploadData.amount_gross}
                onChange={(e) => setUploadData({...uploadData, amount_gross: e.target.value})}
                placeholder="0.00"
                className="w-full px-4 py-3 bg-white/5 border-2 border-white/20 rounded-xl text-white"
                data-testid="invoice-amount-input"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-white/60 mb-2">MwSt-Satz (%)</label>
              <select
                value={uploadData.vat_rate}
                onChange={(e) => setUploadData({...uploadData, vat_rate: e.target.value})}
                className="w-full px-4 py-3 bg-white/5 border-2 border-white/20 rounded-xl text-white"
                data-testid="invoice-vat-select"
              >
                <option value="19.0">19 %</option>
                <option value="7.0">7 %</option>
                <option value="0.0">0 % (steuerbefreit)</option>
              </select>
            </div>
          </div>
          
          <button
            onClick={handleUpload}
            disabled={uploading || !uploadFile}
            data-testid="invoice-upload-button"
            className="w-full md:w-auto px-6 py-3 bg-[#10B981] text-black rounded-xl font-bold hover:bg-[#059669] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Upload className="w-5 h-5" />
            {uploading ? 'Wird hochgeladen...' : 'Rechnung hochladen'}
          </button>
          
          {/* E-Mail Info */}
          <div className="mt-6 p-4 bg-blue-500/10 border-2 border-blue-500/30 rounded-xl flex items-start gap-3">
            <Mail className="w-5 h-5 text-blue-400 mt-1 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-medium text-blue-400 mb-1">Automatischer Import via E-Mail</div>
              <div className="text-sm text-white/60">
                Leite Rechnungen von Stripe & Astria automatisch weiter an:{' '}
                <span className="font-mono text-blue-400 break-all">invoices@snapshot-ai.com</span>
              </div>
              <div className="text-xs text-white/40 mt-1">
                (E-Mail-Parser wird in Phase 4b implementiert)
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 flex gap-2 overflow-x-auto scrollbar-hide -mx-4 md:mx-0 px-4 md:px-0"
             style={{
               scrollbarWidth: 'none',
               msOverflowStyle: 'none',
               WebkitOverflowScrolling: 'touch'
             }}>
          {['all', 'stripe', 'astria', 'manual'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`filter-${f}`}
              className={`px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap flex-shrink-0 ${
                filter === f
                  ? 'bg-[#10B981] text-black'
                  : 'bg-white/5 text-white/60 hover:text-white hover:bg-white/10'
              }`}
            >
              {f === 'all' ? 'Alle' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        {/* Invoices List */}
        <div className="bg-black border-2 border-white/10 rounded-xl grain-texture">
          <div className="p-4 md:p-6">
            <h2 className="text-xl md:text-2xl font-bold mb-6 flex items-center justify-between">
              <span>Rechnungen ({invoices.length})</span>
            </h2>
            
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin w-12 h-12 border-4 border-[#10B981] border-t-transparent rounded-full mx-auto"></div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="text-center py-12 text-white/40">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Keine Rechnungen vorhanden</p>
              </div>
            ) : (
              <>
                {/* Desktop: Tabelle */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-white/10">
                        <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Datum</th>
                        <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Nummer</th>
                        <th className="text-left py-3 px-4 text-white/60 font-medium text-sm">Anbieter</th>
                        <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Brutto (€)</th>
                        <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">Netto (€)</th>
                        <th className="text-right py-3 px-4 text-white/60 font-medium text-sm">MwSt (€)</th>
                        <th className="text-center py-3 px-4 text-white/60 font-medium text-sm">MwSt-Satz</th>
                        <th className="text-center py-3 px-4 text-white/60 font-medium text-sm">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map((inv, idx) => (
                        <tr 
                          key={inv.id} 
                          className="border-b border-white/5 hover:bg-white/5 transition-colors"
                          data-testid={`invoice-row-${idx}`}
                        >
                          <td className="py-4 px-4 text-sm">{formatDate(inv.invoice_date)}</td>
                          <td className="py-4 px-4 text-sm font-mono">{inv.invoice_number}</td>
                          <td className="py-4 px-4 text-sm font-medium">{inv.vendor_name}</td>
                          <td className="py-4 px-4 text-right text-sm font-bold">€{inv.amount_gross?.toFixed(2)}</td>
                          <td className="py-4 px-4 text-right text-sm">€{inv.amount_net?.toFixed(2)}</td>
                          <td className="py-4 px-4 text-right text-sm text-white/60">€{inv.vat_amount?.toFixed(2)}</td>
                          <td className="py-4 px-4 text-center text-sm">{inv.vat_rate}%</td>
                          <td className="py-4 px-4 text-center">{getStatusBadge(inv.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile: Karten */}
                <div className="md:hidden space-y-4">
                  {invoices.map((inv, idx) => (
                    <div 
                      key={inv.id}
                      className="bg-white/5 border border-white/10 rounded-lg p-4"
                      data-testid={`invoice-card-${idx}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="font-bold text-lg">{inv.vendor_name}</div>
                          <div className="text-xs text-white/50 mt-1">{formatDate(inv.invoice_date)}</div>
                        </div>
                        {getStatusBadge(inv.status)}
                      </div>
                      
                      <div className="space-y-2 text-sm mb-3 pb-3 border-b border-white/10">
                        <div className="flex justify-between">
                          <span className="text-white/60">Nummer:</span>
                          <span className="font-mono">{inv.invoice_number}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-white/60 text-xs mb-1">Brutto</div>
                          <div className="font-bold text-lg">€{inv.amount_gross?.toFixed(2)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/60 text-xs mb-1">Netto</div>
                          <div className="font-bold text-lg">€{inv.amount_net?.toFixed(2)}</div>
                        </div>
                        <div>
                          <div className="text-white/60 text-xs mb-1">MwSt</div>
                          <div className="font-medium">€{inv.vat_amount?.toFixed(2)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-white/60 text-xs mb-1">MwSt-Satz</div>
                          <div className="font-medium">{inv.vat_rate}%</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
